import {
  createTicketAttachmentApi,
  createTicketMultiAttachmentApi,
  createTicketMultiPartAttachmentCompleteApi,
  createTicketMultiPartAttachmentInitiateApi,
  createTicketMultiPartAttachmentUploadApi,
} from 'api/tickets';
import type {
  CreateTicketAttachmentRequestBodyDTO,
  CreateTicketMultiPartAttachmentUploadResponseBodyDTO,
} from 'declarations/dto/tickets';

const SMALL_ATTACHMENT_MAX_BYTES = 10_000_000;
const MULTI_PART_CHUNK_BYTES = 6_000_000;

function createChunks(file: File, chunkSize: number): Blob[] {
  const chunks: Blob[] = [];
  let start = 0;
  while (start < file.size) {
    const end = Math.min(start + chunkSize, file.size);
    chunks.push(file.slice(start, end));
    start = end;
  }
  return chunks;
}

function blobToBase64Async(blob: Blob): Promise<string> {
  return new Promise((resolve, reject): void => {
    const fileReader = new FileReader();
    fileReader.onerror = (): void => {
      if (fileReader.error) {
        reject(
          fileReader.error instanceof Error ? fileReader.error : new Error(String(fileReader.error))
        );
      } else {
        reject(new Error('FileReader error'));
      }
    };
    fileReader.onloadend = (): void => {
      const dataUrl = fileReader.result as string;
      const base64 = dataUrl.substring(dataUrl.indexOf(',') + 1);
      resolve(base64);
    };
    fileReader.readAsDataURL(blob);
  });
}

function fileListToArray(list: FileList | File[] | null | undefined): File[] {
  if (!list) {
    return [];
  }
  if (Array.isArray(list)) {
    return list;
  }
  return Array.from(list);
}

function fileIdentityKey(file: File): string {
  return `${file.name}\0${file.size}\0${file.lastModified}`;
}

/** Drops exact duplicates (same name, size, and last modified) while preserving order. */
function dedupeFilesPreserveOrder(files: readonly File[]): File[] {
  const seen = new Set<string>();
  const out: File[] = [];
  for (const f of files) {
    const key = fileIdentityKey(f);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    out.push(f);
  }
  return out;
}

function buildSmallAttachmentBody(
  file: File,
  attachmentTypeCode: string,
  notes: string,
  assignToOppositeUser: boolean
): CreateTicketAttachmentRequestBodyDTO {
  return {
    'Attachment.File': file,
    'Attachment.AttachmentTypeCode': attachmentTypeCode,
    'Attachment.Notes': notes,
    AssignToOppositeUser: assignToOppositeUser,
  };
}

async function postSmallAttachments(
  ticketId: string,
  files: File[],
  attachmentTypeCode: string,
  notes: string,
  assignToOppositeUser: boolean
): Promise<void> {
  if (files.length === 0) {
    return;
  }
  if (files.length === 1) {
    await createTicketAttachmentApi(
      ticketId,
      buildSmallAttachmentBody(files[0], attachmentTypeCode, notes, assignToOppositeUser)
    );
    return;
  }
  await createTicketMultiAttachmentApi(
    ticketId,
    files.map(f => buildSmallAttachmentBody(f, attachmentTypeCode, notes, assignToOppositeUser))
  );
}

async function postLargeAttachmentMultipart(
  ticketId: string,
  attachment: File,
  attachmentTypeCode: string,
  notes: string,
  assignToOppositeUser: boolean
): Promise<void> {
  const initiateResponse = await createTicketMultiPartAttachmentInitiateApi(ticketId, {
    attachmentTypeCode,
    fileContentType: attachment.type,
  });

  const partNumberETags: CreateTicketMultiPartAttachmentUploadResponseBodyDTO[] = [];
  const chunks = createChunks(attachment, MULTI_PART_CHUNK_BYTES);
  let partNumber = 1;

  for (const chunk of chunks) {
    const content = await blobToBase64Async(chunk);
    const uploadResponse = await createTicketMultiPartAttachmentUploadApi({
      fileKey: initiateResponse.fileKey,
      uploadId: initiateResponse.uploadId,
      partNumber,
      content,
    });
    partNumberETags.push(uploadResponse);
    partNumber += 1;
  }

  await createTicketMultiPartAttachmentCompleteApi(ticketId, {
    fileKey: initiateResponse.fileKey,
    uploadId: initiateResponse.uploadId,
    partNumberETags,
    attachmentTypeCode,
    notes,
    assignToOppositeUser,
    originalFileName: attachment.name,
  });
}

export type UploadTicketAttachmentsFromFilesParamsT = {
  ticketId: string;
  attachmentTypeCode: string;
  notes: string;
  assignToOppositeUser: boolean;
  attachmentFileList?: FileList | File[] | null | undefined;
  attachmentPhotoList?: FileList | File[] | null | undefined;
};

/**
 * Uploads ticket attachments: one batch request for all small files, multipart per large file.
 */
export async function uploadTicketAttachmentsFromFiles({
  ticketId,
  attachmentTypeCode,
  notes,
  assignToOppositeUser,
  attachmentFileList,
  attachmentPhotoList,
}: UploadTicketAttachmentsFromFilesParamsT): Promise<void> {
  const ordered = [...fileListToArray(attachmentPhotoList), ...fileListToArray(attachmentFileList)];
  const uniqueFiles = dedupeFilesPreserveOrder(ordered);

  const small = uniqueFiles.filter(f => f.size < SMALL_ATTACHMENT_MAX_BYTES);
  const large = uniqueFiles.filter(f => f.size >= SMALL_ATTACHMENT_MAX_BYTES);

  await postSmallAttachments(ticketId, small, attachmentTypeCode, notes, assignToOppositeUser);

  for (const file of large) {
    await postLargeAttachmentMultipart(
      ticketId,
      file,
      attachmentTypeCode,
      notes,
      assignToOppositeUser
    );
  }
}
